'use client';
import { AccordionTrigger, Button } from 'ui';
import {
  AuthenticateFormData,
  AuthenticateFormProps,
} from './AuthenticateForm';
import { PiramidLogo } from './PiramidLogo';
import { AuthLink } from './AuthLink';

export const TOS_URL = 'https://www.piramid.app/legales';

export type AuthenticateProps = {
  title: string;
  description: string;
  inverseAction:
    | {
        type: 'callback';
        callback: () => void;
        destination: string;
        question: string;
      }
    | {
        type: 'path';
        path: string;
        destination: string;
        question: string;
      };
  back: {
    label: string;
  };
  submit: {
    label: string;
    handle: (data: AuthenticateFormData) => Promise<any>;
    forceVerification?: boolean;
  };
  callbacks: {
    onDone: () => void;
  };
} & Pick<AuthenticateFormProps, 'context'>;

export const AuthenticateMoreOptionsTrigger = () => {
  return (
    <AccordionTrigger
      chevronIconClassName='text-foreground'
      className='no-underline flex flex-row items-center justify-center w-78 py-2 focus:no-underline'
    >
      <span className='me-1 text-foreground text-base_brand font-semibold pointer-events-auto'>
        Más opciones
      </span>
    </AccordionTrigger>
  );
};

export const AuthenticationIDPContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='flex flex-col space-y-3 w-78'>{children}</div>;
};

export const AuthenticateTOS = () => {
  return (
    <p className='text-[10px] w-78 mt-4 text-stone-300'>
      Al hacer clic en Continuar, reconoce que ha leído y acepta los{' '}
      <a href={TOS_URL} target='_blank'>
        <span className='underline hover:text-stone-400'>
          Términos de servicio
        </span>{' '}
        y la{' '}
        <span className='underline hover:text-stone-400'>
          Política de privacidad
        </span>
      </a>{' '}
      de Piramid.
    </p>
  );
};

export const AuthenticateLogo = ({ src }: { src?: string }) => {
  return (
    <>
      {src ? (
        <img src={src} alt='Logo' className='w-20 h-20' />
      ) : (
        <div className='w-10 h-10 '>
          <PiramidLogo variant='black' />
        </div>
      )}
    </>
  );
};

export const AuthenticateContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return <div className='w-[321px]'>{children}</div>;
};

export const AuthenticateTitle = ({ children }: { children: string }) => {
  return (
    <h1 className='font-semibold text-[2rem] my-4 text-foreground tracking-tighter'>
      {children}
    </h1>
  );
};

export const AuthenticateDescription = ({ children }: { children: string }) => {
  return (
    <p className='text-stone-300 font-semibold mb-4 text-[22px] tracking-[-0.03em] leading-[1.875rem] whitespace-pre-line'>
      {children}
    </p>
  );
};

export const AuthenticationMoreOptions = ({
  handleContinueWithEmail,
  inverseAction,
  withEmailContinue = true,
}: {
  handleContinueWithEmail: () => void;
  inverseAction?: AuthenticateProps['inverseAction'];
  withEmailContinue?: boolean;
}) => {
  return (
    // Add accordion if more options are needed
    <>
      {withEmailContinue && (
        <Button
          className='bg-beige text-foreground hover:bg-beige w-full h-11.1 px-4 py-2 mt-2 rounded-[.625rem]'
          onClick={handleContinueWithEmail}
        >
          <span className='ml-2 font-semibold text-[.875rem]'>
            Continuar con email
          </span>
        </Button>
      )}

      {!!inverseAction && (
        <div className='mt-6 text-center'>
          {inverseAction.type === 'callback' ? (
            <AuthLink
              {...inverseAction}
              asButton
              onClick={inverseAction.callback}
            />
          ) : (
            <AuthLink asButton={false} {...inverseAction} />
          )}
        </div>
      )}
    </>
  );
};
